import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type ContentStatus =
  | 'draft'
  | 'generating'
  | 'ready'
  | 'scheduled'
  | 'dispatching'
  | 'published'
  | 'failed';

export type Content = {
  id: string;
  site_id: string | null;
  site_name?: string | null;
  site_url?: string | null;
  title: string;
  slug: string | null;
  body_html: string | null;
  excerpt: string | null;
  seo_title: string | null;
  seo_description: string | null;
  focus_keyword: string | null;
  language: string | null;
  tone: string | null;
  audience: string | null;
  article_type: string | null;
  template_type?: string | null;
  section_length: string | null;
  ai_provider: string | null;
  ai_model: string | null;
  ai_credits_consumed: number | null;
  featured_image_r2_key?: string | null;
  featured_image_alt?: string | null;
  status: ContentStatus;
  published_at: number | null;
  remote_post_id: number | null;
  remote_post_url: string | null;
  error_message: string | null;
  created_at: number;
  updated_at: number;
};

export type CreateContentInput = {
  site_id?: string | null;
  focus_keyword: string;
  title?: string;
  language?: string;
  tone?: string;
  audience?: string;
  pov?: string;
  article_type?: 'general' | 'how_to' | 'list';
  template_type?: string;
  section_length?: 'short' | 'medium' | 'long';
  include_faq?: boolean;
  custom_instructions?: string;
  ai_model?: string;
};

export type GenerateOptions = {
  ai_model: string;
  include_faq?: boolean;
  include_meta?: boolean;
  custom_instructions?: string;
  dry_run?: boolean;
  web_grounded?: boolean;
  web_recency?: 'day' | 'week' | 'month' | 'year' | 'any';
  image_options?: {
    featured?: boolean;
    body_count?: number;
    body_layout?: 'full_width' | 'float_left' | 'float_right' | 'alternating';
    pricing_id?: string;
  };
};

export type GenerateResponse = {
  content?: Content;
  credits_consumed?: number;
  credit_balance?: number;
  word_count?: number;
  estimate_credits?: number;
  model?: { id: string; label: string; credits_per_word: number };
  length?: { min: number; max: number; target: number };
};

export type ModelInfo = {
  id: string;
  label: string;
  provider: string;
  credits_per_word: number;
  family: string;
  new?: boolean;
  premium?: boolean;
  featured?: boolean;
  note?: string;
};

export type LengthPreset = {
  id: 'short' | 'medium' | 'long';
  label: string;
  min: number;
  max: number;
  target: number;
};

export function useContentList(params: { status?: string; site_id?: string } = {}) {
  return useQuery({
    queryKey: ['content', params],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.site_id) qs.set('site_id', params.site_id);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api.get<{ content: Content[] }>(`/api/app/content${suffix}`);
    },
  });
}

export function useContent(id: string | null) {
  return useQuery({
    queryKey: ['content', 'detail', id],
    queryFn: () => api.get<{ content: Content }>(`/api/app/content/${id}`),
    enabled: !!id,
  });
}

export function useModels() {
  return useQuery({
    queryKey: ['content', 'pricing'],
    queryFn: () =>
      api.get<{
        models: ModelInfo[];
        length_presets: LengthPreset[];
        families: Array<{ id: string; label: string }>;
      }>('/api/app/content/pricing/models'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContentInput) =>
      api.post<{ content: Content }>('/api/app/content', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content'] }),
  });
}

export function useUpdateContent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Content>) =>
      api.patch<{ content: Content }>(`/api/app/content/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', id] });
    },
  });
}

export function useDeleteContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/app/content/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content'] }),
  });
}

export function useGenerateContent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: GenerateOptions) =>
      api.post<GenerateResponse>(`/api/app/content/${id}/generate`, opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', id] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useDispatchContent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: {
      site_id?: string;
      status?: 'publish' | 'draft' | 'pending';
      category_ids?: number[];
      tag_ids?: number[];
    }) =>
      api.post<{ content: Content; remote_post_id: number; remote_post_url: string }>(
        `/api/app/content/${id}/dispatch`,
        opts,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', id] });
    },
  });
}
